/** @format */
import { Counter } from 'promjs/counter';
import prom from 'promjs/index';

import { getConfig } from '@toucan/common/services/nextjs/settings';

import { client } from '@toucan/client/global/axios';
import { AvailableMarket } from '@toucan/client/global/market';
import {
    CheckoutType,
    PaymentGatewayType,
} from '@toucan/client/global/transaction';

const registry = prom();

export const paymentGatewayAttemptedMetric = registry.create(
    'counter',
    'toucan_payment_gateway_attempted',
    'A counter of payment gateway attempts'
);

export const paymentGatewayFailedMetric = registry.create(
    'counter',
    'toucan_payment_gateway_failure',
    'A counter of payment gateway failures'
);

export const checkoutCreateAttemptedMetric = registry.create(
    'counter',
    'toucan_checkout_create_attempted',
    'A counter of checkout creation attempts'
);

export const checkoutCreateFailedMetric = registry.create(
    'counter',
    'toucan_checkout_create_failure',
    'A counter of checkout creation failures'
);

export const checkoutFinalizeAttemptedMetric = registry.create(
    'counter',
    'toucan_checkout_finalize_attempted',
    'A counter of checkout finalize request attempts'
);

export const checkoutFinalizeFailedMetric = registry.create(
    'counter',
    'toucan_checkout_finalize_failure',
    'A counter of checkout finalize request failures'
);

/**
 * We need to define a default value of 0 for each market/gateway/checkout type
 * this will loop over each market, checkout type and gateway and set the counter to 0.
 */
function setDefaultMetricValues(
    metric: Counter,
    availableMarkets?: AvailableMarket[]
) {
    const marketCodes = availableMarkets?.map((market) => market.code);
    const checkoutTypes = Object.values(CheckoutType);
    const paymentGateways = Object.values(PaymentGatewayType);

    marketCodes?.forEach((market) => {
        checkoutTypes.forEach((checkoutType) => {
            paymentGateways.forEach((gateway) => {
                metric.add(0, { market, checkoutType, gateway });
            });
        });
    });
}

/**
 * Sends metrics to the Prometheus gateway
 */
export function sendMetrics(): Promise<void> | undefined {
    const { publicRuntimeConfig } = getConfig();

    if (
        publicRuntimeConfig.dev ||
        publicRuntimeConfig.prometheusGatewayEnabled !== 'true'
    ) {
        return;
    }

    // THIS SPECIFICALLY HAS TO HAVE A TRAILING SLASH OR ELSE IT WILL NOT UPDATE
    // THE METRICS, HTTP METHOD DOES NOT MATTER.
    //
    // See:
    // https://github.com/weaveworks/prom-aggregation-gateway/blob/master/cmd/prom-aggregation-gateway/main.go#L277
    return client.post('/client-metrics/', registry.metrics()).then(() => {
        // We want to reset the metric information so we don't keep sending the same
        // results multiple times, which will skew the data
        registry.reset();
    });
}

/**
 * Starts a timeout which sends metrics to the Prometheus gateway
 */
export function startMetrics(
    availableMarkets: AvailableMarket[]
): ReturnType<typeof setInterval> | null {
    const { publicRuntimeConfig } = getConfig();

    if (publicRuntimeConfig.prometheusGatewayEnabled !== 'true') {
        return null;
    }

    [
        paymentGatewayFailedMetric,
        paymentGatewayAttemptedMetric,
        checkoutCreateAttemptedMetric,
        checkoutCreateFailedMetric,
        checkoutFinalizeAttemptedMetric,
        checkoutFinalizeFailedMetric,
    ].forEach((metric) => {
        setDefaultMetricValues(metric, availableMarkets);
    });

    return setInterval(
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- Legacy code: do not use as an example.
        sendMetrics,
        Number(publicRuntimeConfig.prometheusGatewayMetricTimeout)
    );
}
